import {
  ContentCopyOutlined as ContentCopyIcon,
  Refresh as RefreshIcon,
} from "@/components/icons";
import * as React from "react";

import i18n from "@/i18n";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";

type Props = { children: React.ReactNode };
type State = {
  error: Error | null;
  info: React.ErrorInfo | null;
};

/** Top-level ErrorBoundary for the /app tree.
 *
 *  React production builds mangle error messages (the famous "Minified
 *  React error #NNN"), which makes live debugging brutal. This boundary
 *  captures the raw error + the React componentStack, surfaces them in
 *  a copy-ready block so we can paste it back to the maintainer, and
 *  offers a one-click reload. Runs alongside Vite source maps so the
 *  line numbers in the stack point at real source.
 */
export class AppErrorBoundary extends React.Component<Props, State> {
  override state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    this.setState({ info });
    // Mirror into the console so it's captured by whatever log sink
    // the user has wired up (browser devtools, Datadog RUM, etc.).
    console.error("AppErrorBoundary caught", { error, info });
  }

  handleReset = () => {
    this.setState({ error: null, info: null });
  };

  override render(): React.ReactNode {
    const { error, info } = this.state;
    if (!error) return this.props.children;
    const diagnostics = formatDiagnostics(error, info);
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 900, mx: "auto" }}>
        <Stack sx={{ gap: 2 }}>
          <Alert severity="error">
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {i18n.t("errors.crash_title", "Something broke while rendering this page.")}
            </Typography>
            <Typography variant="body2">
              {i18n.t(
                "errors.crash_body",
                "The rest of the app is still usable — try reloading or going back to sessions. If it keeps happening, copy the diagnostics below and send them to us.",
              )}
            </Typography>
          </Alert>

          <Stack direction="row" sx={{ gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
            >
              {i18n.t("errors.reload_page", "Reload page")}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                this.handleReset();
                window.location.assign("/app");
              }}
            >
              {i18n.t("errors.back_to_app", "Back to app")}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(diagnostics);
                } catch {
                  /* clipboard API can be disabled in iframes; user
                   * can still select the text manually */
                }
              }}
            >
              {i18n.t("errors.copy_diagnostics", "Copy diagnostics")}
            </Button>
          </Stack>

          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                {i18n.t("errors.diagnostics", "Diagnostics")}
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2,
                  fontSize: 12,
                  fontFamily: "monospace",
                  bgcolor: "background.default",
                  borderRadius: 1,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  maxHeight: "60vh",
                  overflow: "auto",
                }}
              >
                {diagnostics}
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    );
  }
}

function formatDiagnostics(error: Error, info: React.ErrorInfo | null): string {
  const parts: string[] = [];
  parts.push(`URL: ${window.location.href}`);
  parts.push(`UA: ${navigator.userAgent}`);
  parts.push(`When: ${new Date().toISOString()}`);
  parts.push("");
  parts.push(`Error: ${error.name}: ${error.message}`);
  if (error.stack) {
    parts.push(error.stack);
  }
  if (info?.componentStack) {
    parts.push("");
    parts.push("Component stack:");
    parts.push(info.componentStack);
  }
  return parts.join("\n");
}
